import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { AuthorLink } from "../components/blog/AuthorLink";
import { CategoryLink } from "../components/blog/CategoryLink";
import { ArticleAuthorBox } from "../components/blog/ArticleAuthorBox";
import { ArticleBody } from "../components/blog/ArticleBody";
import { ArticleCta } from "../components/blog/ArticleCta";
import { ArticleFaq } from "../components/blog/ArticleFaq";
import { ArticleSources } from "../components/blog/ArticleSources";
import { ArticleToc } from "../components/blog/ArticleToc";
import { ReadingProgress } from "../components/blog/ReadingProgress";
import { RelatedPosts } from "../components/blog/RelatedPosts";
import { ShareBar } from "../components/blog/ShareBar";
import { ShortAnswer } from "../components/blog/ShortAnswer";
import { buildToc } from "../components/blog/toc";
import { AutoScale } from "../components/site/AutoScale";
import { Footer } from "../components/site/Footer";
import { Header } from "../components/site/Header";
import { NotFoundView } from "../components/site/NotFoundView";
import { ScrollToTop } from "../components/site/ScrollToTop";
import { SpecialOffer } from "../components/home/SpecialOffer";
import { ArticleStatusNotice } from "../components/blog/ArticleStatusNotice";
import { countWords, formatDate, getRelated, isIndexable, loadPost, publicDate, readingMinutes } from "../content/blog";
import { routes } from "../lib/nav";
import { SITE_URL } from "../lib/site";
import { PUBLISHER_NODE, ROBOTS_INDEX, WEBSITE_ID, breadcrumbNode, toAbsoluteUrl } from "../lib/blog-seo";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await loadPost(params.slug);
    if (!post) throw notFound();
    return { post, related: getRelated(params.slug) };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Статья не найдена — блог HAY.agency" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const { post } = loaderData;
    const url = `${SITE_URL}/blog/${params.slug}`;
    const cover = post.cover;
    const ogImage = toAbsoluteUrl(cover.variants?.og ?? cover.src);
    const sixteenNine = toAbsoluteUrl(cover.variants?.sixteenNine ?? cover.src);
    const square = toAbsoluteUrl(cover.variants?.square ?? cover.src);
    const fourThree = toAbsoluteUrl(cover.variants?.fourThree ?? cover.src);
    const authorUrl = toAbsoluteUrl(post.author.url);
    const coverAlt = cover.alt || post.title;
    const minutes = readingMinutes(post);
    const published = publicDate(post);
    const hasFaq = Boolean(post.faq && post.faq.length > 0);

    const graph: Record<string, unknown>[] = [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: post.seoTitle,
        description: post.seoDescription,
        inLanguage: "ru-RU",
        isPartOf: { "@id": WEBSITE_ID },
        primaryImageOfPage: { "@id": `${url}#primaryimage` },
        breadcrumb: { "@id": `${url}#breadcrumbs` },
      },
      {
        "@type": "ImageObject",
        "@id": `${url}#primaryimage`,
        url: sixteenNine,
        contentUrl: sixteenNine,
        width: cover.width,
        height: cover.height,
        caption: coverAlt,
      },
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        url,
        mainEntityOfPage: { "@id": `${url}#webpage` },
        headline: post.title,
        description: post.seoDescription,
        inLanguage: "ru-RU",
        isAccessibleForFree: true,
        ...(published ? { datePublished: published } : {}),
        ...(post.dateModified ? { dateModified: post.dateModified } : {}),
        articleSection: post.category.name,
        keywords: post.tags,
        thumbnailUrl: ogImage,
        image: [square, fourThree, sixteenNine],
        author: {
          "@type": post.author.type,
          "@id": `${authorUrl}#author`,
          name: post.author.name,
          url: authorUrl,
        },
        publisher: PUBLISHER_NODE,
        wordCount: countWords(post),
        timeRequired: `PT${minutes}M`,
      },
      breadcrumbNode(url, [
        ["Главная", "/"],
        ["Блог", "/blog"],
        [post.category.name, `/blog/${post.category.slug}`],
        [post.title, url],
      ]),
    ];

    if (hasFaq) {
      graph.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: post.faq!.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      });
    }

    return {
      meta: [
        { title: post.seoTitle },
        { name: "description", content: post.seoDescription },
        { name: "robots", content: isIndexable(post) ? ROBOTS_INDEX : "noindex, follow" },
        { property: "og:title", content: post.seoTitle },
        { property: "og:description", content: post.seoDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "HAY.agency" },
        { property: "og:locale", content: "ru_RU" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: coverAlt },
        { property: "article:author", content: authorUrl },
        { property: "article:section", content: post.category.name },
        ...(published ? [{ property: "article:published_time", content: published }] : []),
        ...(post.dateModified
          ? [{ property: "article:modified_time", content: post.dateModified }]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.seoTitle },
        { name: "twitter:description", content: post.seoDescription },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: coverAlt },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }),
        },
      ],
    };
  },
  notFoundComponent: ArticleNotFound,
  component: ArticlePage,
});

function ArticlePage() {
  const { post, related } = Route.useLoaderData();
  const toc = buildToc(post.blocks);
  const url = `${SITE_URL}/blog/${post.slug}`;
  const minutes = readingMinutes(post);
  const published = publicDate(post);

  return (
    <AutoScale>
      <div className="min-h-screen bg-background font-sans text-foreground antialiased">
        <ReadingProgress />
        <SpecialOffer />
        <Header />
        <main>
          <article className="px-5 pt-4 lg:px-[21px] lg:pt-[28px]">
            <nav
              aria-label="Хлебные крошки"
              className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden text-[12px] leading-[16px] text-muted-foreground lg:gap-2 lg:text-[14px] lg:leading-[18px]"
            >
              <Link
                to={routes.home}
                className="shrink-0 text-foreground underline underline-offset-[3px] hover:text-brand-text"
              >
                Главная
              </Link>
              <span aria-hidden="true" className="shrink-0">
                /
              </span>
              <Link to="/blog" className="shrink-0 text-foreground underline underline-offset-[3px] hover:text-brand-text">
                Блог
              </Link>
              <span aria-hidden="true" className="shrink-0">
                /
              </span>
              <CategoryLink
                category={post.category}
                className="shrink-0 text-foreground underline underline-offset-[3px] hover:text-brand-text"
              />
              <span aria-hidden="true" className="shrink-0">
                /
              </span>
              <span className="min-w-0 flex-1 truncate whitespace-nowrap">{post.title}</span>
            </nav>

            <header className="mt-8 max-w-[900px] lg:mt-[46px]">
              <div className="label-caps flex flex-wrap items-center gap-3 text-muted-foreground">
                <CategoryLink category={post.category} className="text-brand-text hover:underline" />
                <span aria-hidden="true">•</span>
                {published ? (
                  <time dateTime={published}>{formatDate(published)}</time>
                ) : (
                  <span>Дата публикации не установлена</span>
                )}
                {post.dateModified ? (
                  <>
                    <span aria-hidden="true">•</span>
                    <span>
                      Обновлено <time dateTime={post.dateModified}>{formatDate(post.dateModified)}</time>
                    </span>
                  </>
                ) : null}
                <span aria-hidden="true">•</span>
                <span>Чтение {minutes} мин</span>
                <span aria-hidden="true">•</span>
                <AuthorLink
                  author={post.author}
                  className="underline underline-offset-[3px] transition-colors duration-300 hover:text-brand-text"
                />
              </div>
              <h1 className="section-title mt-5">{post.title}</h1>
              <ArticleStatusNotice status={post.editorialStatus} />
              <p className="mt-5 max-w-[760px] text-[17px] leading-[27px] text-muted-foreground lg:text-[19px] lg:leading-[30px]">
                {post.lead}
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {post.tags.map((t: string) => (
                  <li key={t} className="btn-label bg-secondary px-3 py-2 text-muted-foreground">
                    #{t}
                  </li>
                ))}
              </ul>
            </header>

            <img
              src={post.cover.variants?.sixteenNine ?? post.cover.src}
              srcSet={
                post.cover.variants?.card && post.cover.variants?.sixteenNine
                  ? `${post.cover.variants.card} 800w, ${post.cover.variants.sixteenNine} 1600w`
                  : undefined
              }
              alt={post.cover.alt || post.title}
              width={post.cover.width}
              height={post.cover.height}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              sizes="(max-width: 1024px) 100vw, 1392px"
              className="mt-8 h-[220px] w-full rounded-[8px] object-cover lg:mt-[46px] lg:h-[520px]"
            />

            <div className="mt-10 grid gap-10 lg:mt-[70px] lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)] lg:gap-[60px]">
              <aside className="order-1 min-w-0 lg:order-2">
                <ArticleToc items={toc} />
              </aside>
              <div className="order-2 min-w-0 lg:order-1">
                <ShortAnswer text={post.shortAnswer} status={post.editorialStatus} />
                <div className="mt-10 lg:mt-[54px]">
                  <ArticleBody blocks={post.blocks} />
                </div>
                <ArticleSources sources={post.sources} />
                <ArticleFaq items={post.faq} />
                <ArticleAuthorBox author={post.author} />
                <div className="mt-10 max-w-[760px] lg:mt-[54px]">
                  <ShareBar url={url} title={post.title} />
                </div>
              </div>
            </div>
          </article>

          <div className="mt-14 lg:mt-[110px]">
            <ArticleCta services={post.relatedServices} />
          </div>
          <RelatedPosts posts={related} />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </AutoScale>
  );
}

function ArticleNotFound() {
  return (
    <NotFoundView
      eyebrow="статья не найдена"
      slogans={[
        ["Материал ушёл", "в архив"],
        ["Такой статьи нет,", "но блог на месте"],
        ["404. А знания", "остались"],
      ]}
      description="Возможно, материал переехал или удалён. Посмотрите другие статьи в блоге."
      primary={{ label: "Все статьи", to: "/blog" }}
      secondary={{ label: "На главную", to: "/" }}
      ticker={["ошибка 404", "статья не найдена", "но блог работает", "hay.agency"]}
      jsonLdName="Статья не найдена — блог hay.agency"
    />
  );
}
